import { useEffect, useState } from "react";
import { Loader2, Pencil, ShieldCheck, ShieldOff, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, apiError, imageUrl } from "@/lib/api";
import { NET_WORTH } from "@/components/apply/TrackFields";

const STATS = [["members", "Members"], ["pending", "Pending applications"], ["open_reports", "Open reports"], ["new_7d", "New in 7 days"], ["suspended", "Suspended"], ["admins", "Admins"]];

export const AdminStats = ({ refreshKey }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then(({ data }) => setD(data)).catch(() => {}); }, [refreshKey]);
  return (
    <dl className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="admin-stats">
      {STATS.map(([k, label]) => (
        <div key={k} className="surface p-4" data-testid={`admin-stat-${k}`}>
          <dt className="text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
          <dd className={`font-display mt-1 text-3xl ${k === "open_reports" && d?.[k] ? "text-destructive" : ""}`}>{d ? d[k] : "–"}</dd>
        </div>
      ))}
    </dl>
  );
};

const EditProfile = ({ a, open, onOpenChange, onSaved }) => {
  const [f, setF] = useState({ name: a.name, home_city: a.home_city || "", bio: a.bio || "", net_worth_band: a.application?.net_worth_band || "" });
  const [busy, setBusy] = useState("");
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    setBusy("save");
    try { await api.put(`/admin/users/${a.id}/profile`, { ...f, net_worth_band: f.net_worth_band || null }); toast.success("Profile updated"); onSaved(); onOpenChange(false); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const removeMedia = async (path, label) => {
    if (!window.confirm(`Remove this ${label} from ${a.name}'s profile?`)) return;
    setBusy(path);
    try { await api.post(`/admin/users/${a.id}/remove-media`, { path }); toast.success(`${label} removed`); onSaved(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto border-border bg-card" data-testid={`admin-edit-dialog-${a.id}`}>
        <DialogHeader><DialogTitle className="font-display text-2xl">Edit {a.name.split(" ")[0]}'s profile</DialogTitle><DialogDescription>Changes are saved straight to their profile.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div><label className="field-label" htmlFor="ae-name">Full name</label><input id="ae-name" className="field" maxLength={80} value={f.name} onChange={set("name")} data-testid="admin-edit-name" /></div>
          <div><label className="field-label" htmlFor="ae-city">Home city</label><input id="ae-city" className="field" maxLength={80} value={f.home_city} onChange={set("home_city")} data-testid="admin-edit-city" /></div>
          <div><label className="field-label" htmlFor="ae-bio">Bio</label><textarea id="ae-bio" className="field min-h-[100px]" maxLength={600} value={f.bio} onChange={set("bio")} data-testid="admin-edit-bio" /></div>
          {a.application?.track === "man" && (
            <div><label className="field-label" htmlFor="ae-nw">Net worth band</label>
              <select id="ae-nw" className="field" value={f.net_worth_band} onChange={set("net_worth_band")} data-testid="admin-edit-networth">
                <option value="">Not set</option>{Object.entries(NET_WORTH).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select></div>
          )}
          {(a.photos?.length > 0 || a.video) && (
            <div><p className="field-label">Profile media</p>
              <ul className="grid grid-cols-4 gap-2">
                {a.photos?.map((p) => (
                  <li key={p} className="relative"><img src={imageUrl(p)} alt="" className="aspect-square w-full rounded-lg object-cover" />
                    <button type="button" className="absolute right-1 top-1 rounded-full bg-black/70 p-1.5 text-white" disabled={!!busy} onClick={() => removeMedia(p, "photo")} aria-label="Remove photo" data-testid={`admin-remove-photo-${p.split("/").pop()}`}><Trash2 className="h-3.5 w-3.5" /></button></li>
                ))}
                {a.video && <li className="relative flex aspect-square items-center justify-center rounded-lg bg-secondary text-xs">Video
                  <button type="button" className="absolute right-1 top-1 rounded-full bg-black/70 p-1.5 text-white" disabled={!!busy} onClick={() => removeMedia(a.video, "video")} aria-label="Remove video" data-testid="admin-remove-video"><Trash2 className="h-3.5 w-3.5" /></button></li>}
              </ul></div>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-outline" onClick={() => onOpenChange(false)} data-testid="admin-edit-cancel">Cancel</button>
            <button type="button" className="btn-primary" disabled={!!busy} onClick={save} data-testid="admin-edit-save">{busy === "save" && <Loader2 className="h-4 w-4 animate-spin" />} Save changes</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const MemberAdminTools = ({ a, onChange }) => {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  if (a.status !== "approved") return null;
  const isMe = a.id === user?.id;
  const toggle = async () => {
    const role = a.role === "admin" ? "member" : "admin";
    if (!window.confirm(role === "admin" ? `Make ${a.name} an admin? They'll get full access to the Admin page.` : `Remove ${a.name}'s admin rights?`)) return;
    setBusy(true);
    try { await api.post(`/admin/users/${a.id}/role`, { role }); toast.success(role === "admin" ? `${a.name} is now an admin` : `${a.name} is no longer an admin`); onChange(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap gap-2 border-t border-border pt-4" data-testid={`member-admin-tools-${a.id}`}>
      <button type="button" className="btn-outline h-9 px-3 text-xs" onClick={() => setEditing(true)} data-testid={`admin-edit-${a.id}`}><Pencil className="h-3.5 w-3.5" /> Edit profile</button>
      {!isMe && <button type="button" className="btn-outline h-9 px-3 text-xs" disabled={busy} onClick={toggle} data-testid={`admin-role-${a.id}`}>
        {a.role === "admin" ? <><ShieldOff className="h-3.5 w-3.5" /> Remove admin</> : <><ShieldCheck className="h-3.5 w-3.5" /> Make admin</>}</button>}
      {editing && <EditProfile a={a} open={editing} onOpenChange={setEditing} onSaved={onChange} />}
    </div>
  );
};
